import { useContext, useEffect, useState } from 'react';

import { Subject, Subscription } from 'rxjs';

import { useOnInit } from 'hooks';

import {
  ModalControllerProps,
  ModalsContextProps,
  OpenModalProps,
  UpdateModalProps,
  UseModalsProps,
} from './types';

import { ModalsContext } from '.';

const useModals = (): UseModalsProps => {
  const { modalController, Modals } = useContext(ModalsContext);

  function openModal<T = undefined>(
    modalKey: string,
    props?: OpenModalProps<T>,
  ) {
    modalController.next({
      key: modalKey,
      isOpen: true,
      props,
    });
  }

  function updateModal<T = undefined>(
    modalKey: string,
    updatedProps: UpdateModalProps<T>,
  ) {
    const previousProps = Modals[modalKey]?.props;

    const props = {
      ...previousProps,
      props: { ...previousProps?.props, ...updatedProps.props },
    };

    modalController.next({
      key: modalKey,
      isOpen: true,
      props,
    });
  }

  function closeModal(modalKey: string) {
    modalController.next({
      key: modalKey,
      isOpen: false,
    });
  }

  return {
    openModal,
    updateModal,
    closeModal,
  };
};

const useModalsController = (
  modalsConfig: ModalsContextProps['Modals'],
): ModalsContextProps => {
  const [Modals, setModals] = useState<ModalsContextProps['Modals']>({});
  const [modalController, setModalController] = useState(
    new Subject<ModalControllerProps>(),
  );

  useOnInit(() => {
    setModals({ ...modalsConfig });
  });

  useEffect(() => {
    const modalSubscription: Subscription = modalController.subscribe({
      next: ({ key, isOpen, props }) => {
        setModals(modals => ({
          ...modals,
          [key]: {
            ...Modals[key],
            isOpen,
            props,
          },
        }));
      },
    });

    setModalController(modalController);

    return () => {
      modalSubscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Modals]);

  return {
    Modals,
    modalController,
  };
};

export { useModals, useModalsController };
